import type { UserOnboardingPreferences } from "./onboarding";
import type { UserNotificationPreferences } from "./notification";

export type FilterOperator =
  | 'is'
  | 'is_not'
  | 'contains'
  | 'does_not_contain'
  | 'after'
  | 'before'
  | 'between';

export type FilterFieldType =
  | 'select'
  | 'multi_select'
  | 'date'
  | 'text'
  | 'boolean';

export interface FilterOption {
  value: string;
  label: string;
  icon?: string;
  count?: number;
}

export interface FilterDefinition {
  key: string;
  label: string;
  icon?: string;
  type: FilterFieldType;
  options?: FilterOption[];
  searchable?: boolean;
  optionsMessage?: string;
  group?: string;
}

export interface ActiveFilter {
  id: string;
  filterKey: string;
  operator: FilterOperator;
  value: string | string[];
}

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

export interface GroupConfig {
  key: string | null;
}

export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
}

export type DashboardSlotTemplate = 'full' | 'halves' | 'thirds';

export interface DashboardLayoutSlot {
  id: string;
  chartKey: string | null;
}

export interface DashboardLayoutRow {
  id: string;
  template: DashboardSlotTemplate;
  slots: DashboardLayoutSlot[];
}

export interface DashboardLayout {
  rows: DashboardLayoutRow[];
}

export interface DisplaySettings {
  sort: SortConfig;
  group: GroupConfig;
  columns: ColumnConfig[];
  dashboardLayout?: DashboardLayout;
}

export interface TableView {
  id: string;
  name: string;
  isDefault: boolean;
  isCustom: boolean;
  filters: ActiveFilter[];
  display: DisplaySettings;
  createdAt: string;
  updatedAt: string;
}

export interface TableViewsState {
  views: TableView[];
  activeViewId: string;
}

export interface UserPreferences {
  tableViews?: Record<string, TableViewsState>;
  onboarding?: UserOnboardingPreferences;
  notificationEvents?: UserNotificationPreferences;
}

export const OPERATORS_FOR_TYPE: Record<FilterFieldType, FilterOperator[]> = {
  select: ['is', 'is_not'],
  multi_select: ['is', 'is_not'],
  date: ['after', 'before', 'between'],
  text: ['contains', 'does_not_contain'],
  boolean: ['is', 'is_not'],
};

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  is: 'is',
  is_not: 'is not',
  contains: 'contains',
  does_not_contain: 'does not contain',
  after: 'after',
  before: 'before',
  between: 'between',
};
